import type { FC } from "react";
import { useMemo, useState } from "react";

import { useSelector } from "react-redux";

import { GhostLoader } from "app/components/React/Table/SWReactTableGhostLoader";
import { SWReactTableOptimized } from "app/components/React/Table/SWReactTableOptimized";
import { TableFooter } from "app/pages/industry-analysis/market-builder/tabs/common/TableFooter";
import { Container } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/styled";
import { getConversionRateAvailability } from "app/pages/industry-analysis/overview/widgets/conversion-rate/shared/getConversionRateAvailability";
import { EXCEL_ENDPOINT, ENTITIES_PER_PAGE } from "app/pages/industry-analysis/players/const";
import { parseRawData } from "app/pages/industry-analysis/players/table/parseRawData";
import type { IMarketPlayersState } from "app/pages/industry-analysis/players/types";
import DurationService from "app/services/DurationService";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { allTrackers } from "app/services/track/track";

import { getColumns } from "./columns";
import type { PlayersTableFilters } from "./TableTop";
import { TableTop } from "./TableTop";
import type { HiddenColumns } from "./types";

interface ITable {
    excelEndpoint?: string;
    state: IMarketPlayersState;
    isDaily: boolean;
    isCustomIndustry?: boolean;
}

export const Table: FC<ITable> = (props) => {
    const { excelEndpoint = EXCEL_ENDPOINT, state, isDaily, isCustomIndustry } = props;
    const { rawData, isLoading } = state;
    const [tableFilters, setTableFilters] = useState<PlayersTableFilters>({
        sort: {
            field: "share",
            sortDirection: "desc",
        },
        search: undefined,
        page: 1,
        websiteType: undefined,
    });
    const { sort, search, page, websiteType } = tableFilters;
    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();
    const [hiddenColumns, setHiddenColumns] = useState<HiddenColumns>(new Set<string>());

    const isConversionRateEntitled = getConversionRateAvailability() === "available";
    const showConversionRateColumns = isConversionRateEntitled && rawData?.ConversionRatesAvailable === true;

    const columns = useMemo(
        () =>
            getColumns(
                sort,
                true,
                hiddenColumns,
                isPeriodOverPeriod,
                isDaily,
                isCustomIndustry,
                showConversionRateColumns,
            ),
        [sort, hiddenColumns, isPeriodOverPeriod, isDaily, isCustomIndustry, showConversionRateColumns],
    );

    const handleColumnToggle = (fieldName: string) => {
        const { field } = columns.find((c) => c.field === fieldName);
        const updatedHiddenColumns = new Set(hiddenColumns);

        if (updatedHiddenColumns.has(field)) {
            updatedHiddenColumns.delete(field);
        } else {
            updatedHiddenColumns.add(field);
        }
        setHiddenColumns(updatedHiddenColumns);
    };

    const setSort = (sort) => {
        setTableFilters({ ...tableFilters, sort });
    };

    const setPage = (page) => {
        allTrackers.dispatchTrackEvent("Pagination", "click", `Table pagination`, { page });
        setTableFilters({ ...tableFilters, page });
    };

    const segmentsModule: ISegmentsModule = useSelector((state) => {
        const { segmentsModule } = state;
        return segmentsModule;
    });

    const onSort = ({ field, sortDirection }) => {
        setSort({ field, sortDirection });
    };
    const showPagination = rawData?.Data?.length > ENTITIES_PER_PAGE;

    const { field, sortDirection } = sort;

    const tableData = useMemo(() => {
        const tableData = parseRawData(rawData, segmentsModule, tableFilters);
        return tableData;
    }, [field, sortDirection, rawData, segmentsModule, search, websiteType?.id, page]);

    const table = useMemo(() => {
        return (
            <div data-automation="market-research-players-main-table">
                <SWReactTableOptimized
                    type={"sticky"}
                    tableData={tableData}
                    tableColumns={columns}
                    onSort={onSort}
                />
            </div>
        );
    }, [tableData, columns]);
    return (
        <Container>
            <TableTop
                excelEndpoint={excelEndpoint}
                tableFilters={tableFilters}
                setTableFilters={setTableFilters}
                columns={columns}
                onClickToggleColumns={handleColumnToggle}
            />
            {isLoading ? <GhostLoader /> : table}
            {showPagination ? (
                <TableFooter
                    page={page}
                    setPage={setPage}
                    totalRecords={tableData.TotalCount}
                />
            ) : null}
        </Container>
    );
};
